import {AceLoggerService} from '@logging/ace-logger.service';
import {Component, DestroyRef, forwardRef, inject, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {AceSelectComponent} from '../ace-select/ace-select.component';
import {finalize} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AceLogger} from '@logging/ace-logger';
import {Task} from "@shared/services/api/task.interface";
import {MessageTranslateService} from '@shared/services';
import {EquipmentId} from '@app/graphql-typings/graphql';
import {TasksCache, TasksCacheImpl} from '@shared/data-stores/tasks-cache';

@Component({
  selector: 'app-rules-select',
  templateUrl: './tasks-select.component.html',
  styleUrls: ['./tasks-select.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => TasksSelectComponent)},
    {
      // Use an ancestor's cache when there is one, so a page fetches a task once. Otherwise make our own cache.
      provide: TasksCache,
      useFactory: () => inject(TasksCache, {optional: true, skipSelf: true})
        ?? Injector.create({providers: [{provide: TasksCache, useClass: TasksCacheImpl}], parent: inject(Injector)})
          .get(TasksCache)
    }
  ],
  standalone: false
})
export class TasksSelectComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() equipmentId: EquipmentId;
  @Input() multiSelect = true;
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() placeholder: string;
  @Input() hideSelected = true;
  @Input() tasks: Task[] = [];
  @Input() appendTo: string;


  @ViewChild('aceSelect', { static: true })
  private aceSelect: AceSelectComponent;

  private readonly logger: AceLogger;
  private tasksFetchSubscription: Subscription;

  readonly MAX_ITEMS = 3;
  selectedTasks: Task[];
  loading = false;

  constructor(private tasksCache: TasksCache,
              private translateService: MessageTranslateService,
              private destroyRef: DestroyRef,
              logService: AceLoggerService) {
    this.logger = logService.create(TasksSelectComponent.name);
  }

  onChange: (_) => void = (_) => {};
  onTouched: () => void = () => {};

  aceSelectChange = (selected: any) => {
    this.selectedTasks = selected;
    this.logger.debug('aceSelectChange() selectedTasks', this.selectedTasks);
    this.onChange(this.selectedTasks);
  };

  aceSelectTouched = () => {
    this.logger.debug('aceSelectTouched()');
    this.onTouched();
  };

  ngOnInit(): void {
    if (!this.placeholder) {
      this.placeholder = this.translateService.instant('Tasks');
    }

    this.aceSelect.registerOnChange(this.aceSelectChange);
    this.aceSelect.registerOnTouched(this.aceSelectTouched);
  }

  writeValue(selected: any): void {
    this.selectedTasks = selected;
    this.logger.debug('writeValue() selectedTasks', this.selectedTasks);
    this.aceSelect.writeValue(this.selectedTasks);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Fetch when equipmentId changes with null tasks (parent sets to null on user equipment change)
    if (changes.equipmentId && (!changes.tasks || this.tasks == null)) {
      this.loadTasksByEquipmentId(true);
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.aceSelect.setDisabledState(isDisabled);
  }

  private loadTasksByEquipmentId(clearSelectedIfNeeded: boolean): void {
    this.tasksFetchSubscription?.unsubscribe();
    if (this.equipmentId != null) {
      this.loading = true;
      this.tasksFetchSubscription = this.tasksCache.getTasksForEquipment(this.equipmentId)
        .pipe(finalize(() => this.loading = false), takeUntilDestroyed(this.destroyRef))
        .subscribe((tasks: Task[]) => {
          this.tasks = tasks;
          this.logger.debug('loadTasksByEquipmentId() tasks', this.tasks);
        });
    } else {
      this.tasks = [];
      if (clearSelectedIfNeeded) {
        this.aceSelect.writeValue([]);
        this.aceSelectChange([]);
      }
    }
  }

  compareFn(a: Task, b: Task): boolean {
    return a.task_id === b.task_id;
  }
}
